import { useMemo } from "react";
import { formatRoleLabel } from "../employeeRoleConstants";
import { validateBulkEmployeeRows } from "./BulkEmployeeRequirementsStep";

function lookupLabel(list, id, idKey = "id", nameKey = "name") {
  if (!id) return "";
  const hit = (list || []).find((x) => String(x[idKey]) === String(id));
  return hit?.[nameKey] || "";
}

function fkDisplay(row, lookups, idKey, nameKey, listKey) {
  const fromId = lookupLabel(lookups?.[listKey], row[idKey]);
  if (fromId) return { label: fromId, isNew: false };
  const fromFile = (row[nameKey] || "").trim();
  if (fromFile) return { label: fromFile, isNew: true };
  return { label: "—", isNew: false };
}

function employeeLabel(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || row.work_email || row.email || "Unnamed";
}

export default function BulkEmployeeFinalReview({
  rows,
  lookups,
  onBack,
  onConfirm,
  uploading,
}) {
  const validation = useMemo(() => validateBulkEmployeeRows(rows, lookups), [rows, lookups]);

  const prepared = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        _dept: fkDisplay(r, lookups, "department_id", "department_name", "departments"),
        _desig: fkDisplay(r, lookups, "designation_id", "designation_name", "designations"),
        _loc: fkDisplay(r, lookups, "location_id", "location_name", "locations"),
        role_label: formatRoleLabel(r.role),
      })),
    [rows, lookups]
  );

  const NewBadge = () => (
    <span className="ml-1.5 inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
      NEW
    </span>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h4 className="text-lg font-bold text-slate-900">Final review — create employees</h4>
          <p className="text-sm text-slate-500 mt-1">
            All requirements are satisfied. Confirm once to safely create {rows.length} employee
            {rows.length === 1 ? "" : "s"} with auto-generated employee codes.
          </p>
        </div>
        {validation.allValid ? (
          <span className="text-xs px-3 py-1.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200 font-semibold">
            Validation passed
          </span>
        ) : (
          <span className="text-xs px-3 py-1.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 font-semibold">
            Validation pending — go back and fix
          </span>
        )}
      </div>

      <div className="mt-4 overflow-auto border border-slate-200 rounded-xl">
        <table className="min-w-[1100px] w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">#</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Employee</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Email</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Department</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Designation</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Location</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Role</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Joining</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">CTC</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Banking</th>
              <th className="text-left px-3 py-2 border-b font-semibold text-slate-700">Statutory</th>
            </tr>
          </thead>
          <tbody>
            {prepared.map((r, idx) => {
              const hasBank = Boolean(r.ifsc_code || r.account_number);
              const hasStat = Boolean(r.uan_number || r.pan || r.aadhaar || r.pf_number || r.esic_number);
              return (
              <tr key={idx} className="odd:bg-white even:bg-slate-50/40">
                <td className="px-3 py-2 border-b text-slate-600">{idx + 1}</td>
                <td className="px-3 py-2 border-b font-medium text-slate-900">{employeeLabel(r)}</td>
                <td className="px-3 py-2 border-b text-slate-700">{r.work_email || r.email}</td>
                <td className="px-3 py-2 border-b text-slate-700">
                  {r._dept.label}
                  {r._dept.isNew ? <NewBadge /> : null}
                </td>
                <td className="px-3 py-2 border-b text-slate-700">
                  {r._desig.label}
                  {r._desig.isNew ? <NewBadge /> : null}
                </td>
                <td className="px-3 py-2 border-b text-slate-700">
                  {r._loc.label}
                  {r._loc.isNew ? <NewBadge /> : null}
                </td>
                <td className="px-3 py-2 border-b text-slate-700">{r.role_label}</td>
                <td className="px-3 py-2 border-b text-slate-700">{r.date_of_joining || "—"}</td>
                <td className="px-3 py-2 border-b text-slate-700">{r.annual_ctc ?? "—"}</td>
                <td className="px-3 py-2 border-b text-slate-700">
                  {hasBank ? (
                    <span className="text-xs font-semibold text-emerald-700">
                      {r.ifsc_code || "IFSC"} · ****{(String(r.account_number || "").slice(-4)) || "----"}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 border-b text-slate-700">
                  {hasStat ? (
                    <span className="text-xs font-semibold text-indigo-700">
                      {[r.pan && "PAN", r.uan_number && "UAN", r.aadhaar && "Aadhaar"].filter(Boolean).join(" · ") || "Yes"}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onBack}
          disabled={uploading}
          className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          ← Back to requirements
        </button>
        <button
          type="button"
          disabled={!validation.allValid || uploading}
          onClick={onConfirm}
          className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? "Creating employees…" : `Create ${rows.length} employee${rows.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
